import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// redux
import { useSelector } from "react-redux";

import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

// MUI icons
import AnalyticsIcon from "@mui/icons-material/Analytics";

// assets
import WeightToggleIcon from "../../assets/icons/weightToggle.png";

// components
import DriverAnalysisFilters from "../correlation/DriverAnalysisFilters";

// bucket definitions A1-A8
import { BUCKET_DATA, DEFAULT_BUCKET } from "../../utils/fragranceBucketData";

import TableCellWithInnerSeparator from "../../components/common/analysisModule/TableCellWithInnerSeparator";
import TableCellWithOuterSeparator from "../../components/common/analysisModule/TableCellWithOuterSeparator";

function FragranceDriverTable({ onReset = () => { }, selectedBucket = null, bucketKPIs = {} }) {
    const navigate = useNavigate();
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);


    const [isBucketSelected, setIsBucketSelected] = useState(false);

    const currentBucket = BUCKET_DATA.find((bucket) => bucket.id === (selectedBucket?.value || DEFAULT_BUCKET.id)) || DEFAULT_BUCKET;


    const currentBucketKPIs = bucketKPIs?.[currentBucket.id] || [];

    // Only show KPIs that are available in the study (available_in_study === true)
    const availableKPIs = currentBucketKPIs.filter((kpi) =>
        typeof kpi === "object" && kpi !== null ? kpi.available_in_study === true : true
    );

    // Extract display labels for rendering in the table
    const currentBucketKpiLabels = availableKPIs.map((kpi) =>
        typeof kpi === "string" ? kpi : (kpi.display_label || kpi.backend_variable || "")
    );

    // Extract backend variable codes for the Driver Analysis payload (only available KPIs)
    const currentBucketKpiVariables = availableKPIs.map((kpi) =>
        typeof kpi === "string" ? kpi : (kpi.backend_variable || kpi.display_label || "")
    );

    // A bucket is eligible for Driver Analysis only when it has a minimum of 3 KPIs with data.
    // The button stays disabled until the user selects the bucket and it has >= 3 KPIs.
    // Use the available (true) KPI length for the eligibility check.
    const hasEnoughKpis = currentBucketKpiVariables.length >= 3;
    const isDriverAnalysisDisabled = !isBucketSelected || !hasEnoughKpis;
    const hasKpis = currentBucketKpiLabels.length > 0;
    const kpiCount = currentBucketKpiLabels.length;
    const KPI_ROW_HEIGHT = 45;
    const KPI_ROW_GAP = 6;
    const KPI_BOTTOM_PADDING = 6;
    const MAX_VISIBLE_KPIS = 10;
    const MAX_KPI_HEIGHT = MAX_VISIBLE_KPIS * KPI_ROW_HEIGHT + (MAX_VISIBLE_KPIS - 1) * KPI_ROW_GAP + KPI_BOTTOM_PADDING;
    const renderedKpiCount = Math.max(kpiCount, 1);
    const kpiContentHeight = kpiCount > MAX_VISIBLE_KPIS ? MAX_KPI_HEIGHT : renderedKpiCount * KPI_ROW_HEIGHT + Math.max(renderedKpiCount - 1, 0) * KPI_ROW_GAP + KPI_BOTTOM_PADDING;
    const isKpiScrollable = kpiCount > MAX_VISIBLE_KPIS;

    // Reset bucket selection when switching to another bucket
    useEffect(() => {
        setIsBucketSelected(false);
    }, [selectedBucket]);

    const tableData = [
        {
            attribute: currentBucket.label,
            kpiArea: hasKpis ? currentBucketKpiLabels : ["No KPIs are available for this bucket."],
        },
    ];

    return (
        <>
            {/* Toolbar */}
            <div
                style={{
                    right: "-30px",
                    float: "right",
                    display: "inline-block",
                    position: "relative",
                }}
            >
                {/* Weight (disabled) */}
                <Button
                    size="small"
                    className={"custom-btn me-4 bold-content"}
                    disabled={true}
                    variant="outlined"
                    startIcon={
                        <img
                            className={"invert"}
                            style={{
                                height: "16px",
                            }}
                            src={WeightToggleIcon}
                            alt="Weight"
                        />
                    }
                >
                    Weight
                    <Switch
                        size="small"
                        disabled={true}
                        checked={false}
                    />
                </Button>

                {/* Show Latest Driver Analysis Report */}
                <Button
                    size="small"
                    className={"custom-btn custom-btn-outlined color-blue me-3 bold-content"}
                    variant="outlined"
                    onClick={() =>
                        navigate("/driver-analysis", {
                            state: { isFragranceDriver: true },
                        })
                    }
                    startIcon={<AnalyticsIcon />}
                >
                    Show latest driver analysis report
                </Button>

                {/* Driver Analysis */}
                <DriverAnalysisFilters
                    selectedStudy={selectedStudy}
                    selectedBackendVariableForDriverAnalysis={currentBucketKpiVariables}
                    targetKpi={currentBucket.target_kpi}
                    targetKpiLabel={currentBucket.target_kpi_label}
                    isButtonDisabled={isDriverAnalysisDisabled}
                    isFragrance={true}
                />

                {/* Reset */}
                <Tooltip
                    title="Reset Default Study View"
                    placement="top-end"
                >
                    <Button
                        size="small"
                        className="custom-btn liner-light-blue-background me-4 bold-content"
                        variant="contained"
                        onClick={() => {
                            // Clear the bucket checkbox selection and reset the bucket to default.
                            setIsBucketSelected(false);
                            onReset();
                        }}
                    >
                        Reset
                    </Button>
                </Tooltip>
            </div>

            <div
                style={{
                    width: "100%",
                    overflowX: "auto",
                }}
            >
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: "12px",
                    }}
                >
                    <tbody>
                        {tableData.map((group, groupIndex) => (
                            <tr key={groupIndex}>
                                {/* ATTRIBUTE */}
                                {
                                        <TableCellWithInnerSeparator
                                            value={
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "6px",
                                                        width: "100%",
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        sx={{
                                                            padding: "2px",
                                                        }}
                                                        checked={isBucketSelected}
                                                        onChange={(e) => setIsBucketSelected(e.target.checked)}
                                                        disabled={!hasEnoughKpis}
                                                    />

                                                    <span>{group.attribute}</span>
                                                </div>
                                            }
                                            cellType="td"
                                            testLabel=""
                                            styles={{
                                                main: {
                                                    width: 400,
                                                    minWidth: 400,
                                                    maxWidth: 400,
                                                    verticalAlign: "middle",
                                                    paddingRight: "0", // gap between Attribute and Kpi Area
                                                },

                                                wrapper: {
                                                    height: "100%",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                },

                                                value: {
                                                    width: "100%",
                                                    height: "100%",
                                                    background: "#fff",
                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                    borderRadius: "5px",
                                                    padding: "5px",
                                                    boxSizing: "border-box",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                },
                                            }}
                                            showSeperator={true}
                                        />
                                }

                                    {/* KPI AREA */}
                                    <TableCellWithOuterSeparator
                                        value={
                                        <div
                                            style={{
                                                height: `${kpiContentHeight}px`,
                                                minHeight: `${kpiContentHeight}px`,
                                                maxHeight: `${MAX_KPI_HEIGHT}px`,
                                                width: "100%",
                                                overflowY: isKpiScrollable ? "auto" : "hidden",
                                                overflowX: "auto",
                                                boxSizing: "border-box",
                                                paddingBottom: `${KPI_BOTTOM_PADDING}px`,

                                                // // Hide scrollbar but keep scrolling
                                                // scrollbarWidth: "none",
                                                // msOverflowStyle: "none",
                                            }}
                                            className="kpi-scroll-container"
                                        >
                                            {group.kpiArea.map((area, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        height: "45px",
                                                        minHeight: "45px",
                                                        width: "max-content",
                                                        minWidth: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        background: "#fff",
                                                        whiteSpace: "nowrap",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                        marginBottom: index === group.kpiArea.length - 1 ? 0 : "6px",
                                                        boxSizing: "border-box",
                                                        borderLeft: "6px solid #5B9CF6",
                                                    }}
                                                >
                                                    {area}
                                                </div>
                                            ))}
                                        </div>
                                    }
                                    cellType="td"
                                    testLabel=""
                                    styles={{
                                        main: {
                                            width: "100%",
                                            height: `${kpiContentHeight}px`,
                                            minHeight: `${kpiContentHeight}px`,
                                            maxHeight: `${MAX_KPI_HEIGHT}px`,
                                            padding: 0,
                                        },

                                        wrapper: {
                                            width: "100%",
                                            height: `${kpiContentHeight}px`,
                                            minHeight: `${kpiContentHeight}px`,
                                            maxHeight: `${MAX_KPI_HEIGHT}px`,
                                            alignItems: "flex-start",
                                        },

                                        value: {
                                            width: "100%",
                                            height: `${kpiContentHeight}px`,
                                            minHeight: `${kpiContentHeight}px`,
                                            maxHeight: `${MAX_KPI_HEIGHT}px`,
                                            background: "#fff",
                                            padding: 0,
                                            boxSizing: "border-box",
                                            overflow: "hidden",
                                            borderRadius: "5px",
                                        },
                                    }}
                                    showSeperator={false}
                                />
                            </tr>
                        ))}
                    </tbody>
                </table>
                {/* Bottom notes */}
                <div className="d-flex">
                    <span
                        style={{
                            fontSize: "12px",
                            color: "#495057",
                            margin: 10,
                            fontWeight: 600,
                        }}
                    >
                        NOTE: Select a bucket with a minimum of 3 KPIs to perform Driver Analysis.
                    </span>
                    <span
                        style={{
                            fontSize: "12px",
                            color: "#495057",
                            fontWeight: 600,
                            margin: 10,
                            marginLeft: "auto",
                        }}
                    >
                        **OOP= Overall Opinion of Products | *OOF= Overall Opinion of Fragrance
                    </span>
                </div>
            </div>
        </>
    );
}

// Typechecking props for the FragranceDriverTable
FragranceDriverTable.propTypes = {
    onReset: PropTypes.func,
    selectedBucket: PropTypes.object,
    bucketKPIs: PropTypes.object,
};

export default FragranceDriverTable;
